import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import './i18n/config';
import './App.css';

// Fire a GA4 page_view on every SPA route change.
// The initial page_view is sent by gtag config in index.html.
const AnalyticsTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  return null;
};

import Header from './components/Header';
import Footer from './components/Footer';
import GeoInit from './components/GeoInit';
import Home from './pages/Home';
import Catalog from './pages/Catalog';
import CatalogCategories from './pages/CatalogCategories';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Account from './pages/Account';
import Contact from './pages/Contact';
import Services from './pages/Services';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Gallery from './pages/Gallery';
import Brands from './pages/Brands';
import BrandPage from './pages/BrandPage';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminProducts from './pages/admin/AdminProducts';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBlog from './pages/admin/AdminBlog';
import AdminGallery from './pages/admin/AdminGallery';
import AdminImport from './pages/admin/AdminImport';
import AdminBrands from './pages/admin/AdminBrands';
import AdminServices from './pages/admin/AdminServices';
import AdminContact from './pages/admin/AdminContact';
import AdminCategories from './pages/admin/AdminCategories';
import AdminMedia from './pages/admin/AdminMedia';
import AdminSupply from './pages/admin/AdminSupply';
import AdminStockNotifications from './pages/admin/AdminStockNotifications';
import AdminStockAdjustments from './pages/admin/AdminStockAdjustments';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminReviews from './pages/admin/AdminReviews';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminReturns from './pages/admin/AdminReturns';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminLegalPages from './pages/admin/AdminLegalPages';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import NewsletterConfirm from './pages/NewsletterConfirm';
import LegalPage from './pages/LegalPage';
import CookieBanner from './components/CookieBanner';
import WishlistPage from './pages/WishlistPage';
import ComparePage from './pages/ComparePage';
import { WishlistProvider } from './context/WishlistContext';
import { CompareProvider } from './context/CompareContext';
import CompareFloatingBar from './components/CompareFloatingBar';
import PromoLanding from './pages/PromoLanding';
import HomePromoBanner from './components/HomePromoBanner';

const PublicLayout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <HomePromoBanner />
      <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <CompareProvider>
              <BrowserRouter>
                <AnalyticsTracker />
                <GeoInit />
                <CompareFloatingBar />
                <Routes>
              {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="supply" element={<AdminSupply />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="import" element={<AdminImport />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="brands" element={<AdminBrands />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="contact" element={<AdminContact />} />
              <Route path="stock-notifications" element={<AdminStockNotifications />} />
              <Route path="stock-adjustments" element={<AdminStockAdjustments />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="returns" element={<AdminReturns />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="legal-pages" element={<AdminLegalPages />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
            </Route>

            {/* Public routes */}
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/catalog" element={<PublicLayout><Catalog /></PublicLayout>} />
            <Route path="/catalog/categories" element={<PublicLayout><CatalogCategories /></PublicLayout>} />
            <Route path="/catalog/category/:categoryId" element={<PublicLayout><CatalogCategories /></PublicLayout>} />
            <Route path="/product/:id" element={<PublicLayout><ProductPage /></PublicLayout>} />
            <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
            <Route path="/checkout/success" element={<PublicLayout><CheckoutSuccess /></PublicLayout>} />
            <Route path="/checkout/cancel" element={<PublicLayout><CheckoutCancel /></PublicLayout>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
            <Route path="/verify-email" element={<PublicLayout><VerifyEmail /></PublicLayout>} />
            <Route path="/account" element={<PublicLayout><Account /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
            <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
            <Route path="/brands" element={<PublicLayout><Brands /></PublicLayout>} />
            <Route path="/brands/:slug" element={<PublicLayout><BrandPage /></PublicLayout>} />
            <Route path="/promo/:code" element={<PromoLanding />} />
            <Route path="/legal/:slug" element={<PublicLayout><LegalPage /></PublicLayout>} />
            <Route path="/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />
            <Route path="/compare" element={<PublicLayout><ComparePage /></PublicLayout>} />
            <Route path="/newsletter/confirm/:token" element={<PublicLayout><NewsletterConfirm /></PublicLayout>} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>
            </CompareProvider>
          </WishlistProvider>
        </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
